/**
 * Global axios configuration.
 *
 * Two responsibilities:
 *   1. Set a 15-second default timeout on all axios calls. Previously
 *      requests could hang indefinitely on flaky venue Wi-Fi, freezing UI
 *      spinners forever (the "loading forever" complaint from clients).
 *      15 s is comfortably above p99 latency for our slowest endpoint
 *      (Apple catalog resolve, ~4 s) but short enough that a real
 *      connection loss surfaces as an error the UI can react to.
 *
 *   2. Emit a global `network-status` event whenever a request finishes,
 *      so the Connectivity Pill (see components/ConnectivityPill.js) can
 *      show "reconnecting…" the moment we observe a failure — much faster
 *      than waiting for the browser's `offline` event, which only fires
 *      when the OS declares the whole interface down (Chrome on Android
 *      won't fire it during a DNS blip mid-set).
 *
 * Call `installAxiosDefaults()` exactly once, at app boot, before any
 * axios usage.
 */
import axios from 'axios';

const NETWORK_EVENT = 'app:network-status';
let installed = false;

/**
 * Broadcast a state to any Connectivity listeners.
 * status: 'online' | 'offline' | 'reconnecting'
 */
function emit(status, detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(NETWORK_EVENT, { detail: { status, at: Date.now(), ...detail } }));
  } catch (_) { /* noop — SSR or event constructor blocked */ }
}

export function installAxiosDefaults() {
  if (installed) return;
  installed = true;

  // 15 s default. Individual calls that need longer (uploads, e.g.) can
  // still override with `{ timeout: 60000 }`.
  axios.defaults.timeout = 15000;
  // Cookies are our primary auth vehicle — must be sent cross-site for
  // the preview.emergentagent.com → api.emergentagent.com hop.
  axios.defaults.withCredentials = true;

  axios.interceptors.response.use(
    (res) => {
      emit('online');
      return res;
    },
    (err) => {
      // Distinguish "server responded with an error" from "no response at
      // all". The pill only cares about the latter.
      const noResponse = !err?.response;
      const isTimeout = err?.code === 'ECONNABORTED';
      if (noResponse || isTimeout) {
        emit('reconnecting', {
          url: err?.config?.url,
          reason: isTimeout ? 'timeout' : (err?.message || 'network'),
        });
      }
      return Promise.reject(err);
    }
  );

  // OS-level events — cheap and free.
  window.addEventListener('online', () => emit('online'));
  window.addEventListener('offline', () => emit('offline'));
}

export const NETWORK_STATUS_EVENT = NETWORK_EVENT;
